/**
 * ChatShell — full chat surface inside the morph palette.
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ ← <title>       [+ New chat] [↗] [✕]        │  ← slim header
 *   ├──────────────────────────────────────────────┤
 *   │                                              │
 *   │  <ConversationView>                          │
 *   │    messages, tool chips, approval cards,     │
 *   │    citations, lazy-load older                │
 *   │                                              │
 *   ├──────────────────────────────────────────────┤
 *   │ ✨ [textarea] 📎 🎤 ➤/⏹                     │  ← composer
 *   └──────────────────────────────────────────────┘
 *
 * Usage pattern (per user feedback 2026-05-06): chat → work → chat →
 * work, repeat. Re-opening lands here straight away (don't hide the
 * thread); composer auto-focuses; + New chat is one click for a fresh
 * topic; ↗ hands off to agent.ordo.com for power-user work.
 *
 * [PROD-162] All the chrome lives in that one header strip now. There
 * used to be a second strip under the composer holding a "Clear chat"
 * button and the handoff link — mystery chrome that read as a third
 * way to make the chat go away, next to ← and ✕. "Clear chat" is the
 * header's "New chat"; the handoff link is the ↗ beside it. Modelled
 * on Claude: title + new-chat + close up top, nothing below the input.
 */

import {
  ComposerActionRow,
  ComposerIconButton,
  ComposerSendButton,
  ComposerShell,
  ComposerTextarea,
} from '@ordo/ui/composer-primitives'
import { ArrowLeft, ArrowUp, ExternalLink, Loader2, MessageSquarePlus, Paperclip, Square, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { FileDropZone } from '../attachments/file-drop-zone'
import { ConversationView } from '../conversation/conversation-view'
import { cn } from '../lib/cn'
import { thinkingLabel } from '../lib/thinking-label'
import { useAgentStore } from '../stores/agent-store'
import { useConversationContext } from '../surfaces/conversation-provider'
import type { SlashCommand } from '../types'
import { VoiceButton } from '../voice/voice-button'
import { rewriteAfterSlashPick, SlashAutocomplete } from './slash-autocomplete'

export interface ChatShellProps {
  onClose: () => void
  /** Click → flips paletteOverride on, body shows empty palette
   *  (chips/rows/recent) without losing the active chat. The user
   *  can resume the chat via the resume row in the empty body. */
  onBackToSearch: () => void
  slashCommands?: SlashCommand[]
  /** Resolved deep-link to this conversation in the full agent app
   *  (e.g. https://agent.ordo.com/chat/<id>). Built by AgentSearch
   *  via `buildAgentChatUrl` so localhost vs prod and host overrides
   *  are handled in one place. */
  agentChatUrl: string
}

export function ChatShell({ onClose, onBackToSearch, slashCommands = [], agentChatUrl }: ChatShellProps) {
  const conv = useConversationContext()

  return (
    <FileDropZone
      onFiles={(files) => void conv.pickFiles(files)}
      className="as-chat-shell"
      label="Drop to attach to your message">
      <ChatHeader onBackToSearch={onBackToSearch} onClose={onClose} handoffUrl={agentChatUrl} />
      <div className="as-chat-body">
        <ConversationView
          messages={conv.messages}
          turnState={conv.turnState}
          hasMore={conv.hasMore}
          isLoadingOlder={conv.isLoadingOlder}
          onLoadOlder={() => void conv.loadOlder()}
          onDecideApproval={conv.decideApproval}
          onResolveCard={conv.resolveCard}
          onRetry={conv.isBusy ? undefined : conv.retry}
          onSendSuggestion={conv.isBusy ? undefined : (text) => void conv.submit(text)}
        />
      </div>
      <ChatComposer slashCommands={slashCommands} />
    </FileDropZone>
  )
}

function ChatHeader({
  onBackToSearch,
  onClose,
  handoffUrl,
}: {
  onBackToSearch: () => void
  onClose: () => void
  handoffUrl: string
}) {
  const conv = useConversationContext()
  const setConversationId = useAgentStore((s) => s.setConversationId)
  const setPaletteOverride = useAgentStore((s) => s.setPaletteOverride)
  const setComposerText = useAgentStore((s) => s.setComposerText)
  const conversationId = useAgentStore((s) => s.conversationId)
  const startNewChat = useCallback(() => {
    // Drop the persisted conversation so the next user turn starts a
    // brand new thread. Also clear the composer (in case mid-typing) and
    // bounce back to the launchpad palette so the user sees the empty
    // state, not a stale message list. The conversation engine fetches
    // by `conversationId`, so flipping it to null is enough — no manual
    // message reset needed. Nothing is destroyed server-side: the old
    // thread stays at agent.ordo.com/chats (and behind the ↗ below).
    setConversationId(null)
    setComposerText('')
    setPaletteOverride(true)
  }, [setComposerText, setConversationId, setPaletteOverride])
  return (
    <div className="as-chat-header">
      <button
        type="button"
        className="as-chat-header-btn as-chat-header-btn-icon"
        onClick={(e) => {
          e.stopPropagation()
          onBackToSearch()
        }}
        title="Back to search"
        aria-label="Back to search">
        <ArrowLeft />
      </button>
      {/* Title doubles as the header's flexible spacer (`flex: 1`), so
          the two slots stay pinned to the edges without a filler div. */}
      <span className="as-chat-title">{conv.conversation?.title ?? 'Quick chat'}</span>
      <div className="as-chat-header-actions">
        {/* The one labelled control in the row: it's the action users
            reach for most, and a tooltip-only glyph says nothing on
            touch — where this palette opens full-screen. */}
        <button
          type="button"
          className="as-chat-header-btn"
          onClick={(e) => {
            e.stopPropagation()
            startNewChat()
          }}
          // Stays disabled until the thread has a server id. Before that
          // `setConversationId(null)` is a no-op — the engine is holding
          // the in-flight id internally — so the next message would
          // silently append to this thread instead of starting a new one.
          disabled={!conversationId}>
          <MessageSquarePlus />
          <span>New chat</span>
        </button>
        {/* Deliberately between New chat and Close: it's the only
            non-destructive control here, so a mis-aimed tap opens a tab
            rather than dropping the visible thread. */}
        <a
          href={handoffUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="as-chat-header-btn as-chat-header-btn-icon"
          onClick={(e) => e.stopPropagation()}
          title="Open in Agent"
          aria-label="Open this chat in the full agent app">
          <ExternalLink />
        </a>
        <button
          type="button"
          className="as-chat-header-btn as-chat-header-btn-icon"
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          title="Close"
          aria-label="Close">
          <X />
        </button>
      </div>
    </div>
  )
}

function ChatComposer({ slashCommands }: { slashCommands: SlashCommand[] }) {
  const conv = useConversationContext()
  const composerText = useAgentStore((s) => s.composerText)
  const setComposerText = useAgentStore((s) => s.setComposerText)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Slash autocomplete imperatively claims certain keys (↑ ↓ Enter Tab
  // Esc) when its dropdown is visible. We register its handler here
  // so the textarea's onKeyDown can defer to it before running its
  // own Enter-sends logic.
  const slashKeyHandler = useRef<((e: KeyboardEvent) => boolean) | null>(null)
  const [slashDismissed, setSlashDismissed] = useState(false)

  // Auto-focus when chat shell mounts (re-opening to chat mode → ready
  // to type the next thing).
  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  // Reset the dismissed flag whenever the user starts a new "/…" run.
  useEffect(() => {
    if (!composerText.startsWith('/')) {
      setSlashDismissed(false)
    }
  }, [composerText])

  const isStreaming = conv.turnState === 'streaming' || conv.turnState === 'awaiting' || conv.turnState === 'sending'
  // [PROD-1044] Name what the agent is doing when we know it. Pool turns hide
  // raw tool cards, so this bar is the ONLY place their tool activity shows —
  // "Searching Tasks…" beats a generic phase label for a turn that spends
  // minutes in tools. [PROD-1205] The fallback is desktop's wording now.
  const statusLabel = thinkingLabel({ turnState: conv.turnState, liveActivity: conv.liveActivity })
  const hasUploadingFile = conv.uploads.some((u) => u.status === 'uploading')
  // [PROD-88] Streaming no longer disables the composer — submits while the
  // agent is working queue up and fire as one merged turn when it goes idle.
  const sendDisabled = composerText.trim().length === 0 && conv.uploads.filter((u) => u.status === 'done').length === 0

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Let the slash autocomplete intercept ↑ ↓ Enter Tab Esc when active.
    if (slashKeyHandler.current) {
      const handled = slashKeyHandler.current(e.nativeEvent)
      if (handled) {
        e.preventDefault()
        // Esc consumed by the autocomplete must not also reach the window
        // handler (which would cancel the run / close the palette).
        if (e.nativeEvent.key === 'Escape') {
          e.stopPropagation()
        }
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!sendDisabled && !hasUploadingFile) {
        void conv.submit(composerText)
      }
    }
  }

  const onSlashPick = useCallback(
    (cmd: SlashCommand) => {
      const next = rewriteAfterSlashPick(composerText, cmd)
      setComposerText(next)
      // If the picked command is a fire-and-forget phrase (no args),
      // submit immediately. Otherwise leave focus in textarea for args.
      if (!cmd.takesArgs) {
        requestAnimationFrame(() => {
          void conv.submit(next)
        })
      } else {
        requestAnimationFrame(() => inputRef.current?.focus())
      }
    },
    [composerText, conv, setComposerText],
  )

  const onPaperclipClick = () => fileInputRef.current?.click()
  const onFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files
    if (!list) {
      return
    }
    void conv.pickFiles(Array.from(list))
    e.target.value = ''
  }

  // Paste images / files directly into the textarea — uploads them
  // through the same useFileUpload pipeline as paperclip + drag-drop.
  // Matches agent.ordo.com's paste handler.
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items ?? [])
    const files = items
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null)
    if (files.length > 0) {
      e.preventDefault()
      void conv.pickFiles(files)
    }
  }

  return (
    <div className="as-chat-composer">
      {!slashDismissed && slashCommands.length > 0 ? (
        <SlashAutocomplete
          query={composerText}
          commands={slashCommands}
          onPick={onSlashPick}
          onDismiss={() => setSlashDismissed(true)}
          registerKeyHandler={(handler) => {
            slashKeyHandler.current = handler
          }}
        />
      ) : null}
      {conv.uploads.length > 0 ? (
        <div className="as-chat-uploads">
          {conv.uploads.map((u) => (
            <div
              key={u.localId}
              className={cn('as-chat-upload-chip', u.status === 'error' && 'as-chat-upload-chip-error')}
              title={u.status === 'error' ? u.error : u.file.name}>
              <span className="as-chat-upload-name">{u.file.name}</span>
              {u.status === 'uploading' ? <Loader2 className="as-chat-upload-spin" /> : null}
              <button
                type="button"
                onClick={() => conv.removeUpload(u.localId)}
                className="as-chat-upload-x"
                aria-label="Remove attachment">
                <X />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {/* [PROD-88] Queued-while-busy follow-ups: count + a per-row ✕.
          [PROD-162] The clear-all only appears at 2+ rows — with one
          queued message `clearQueue()` and `removeQueuedAt(0)` are the
          same click, so it was two controls 40px apart doing one thing. */}
      {conv.queueCount > 0 ? (
        <div className="as-chat-queue">
          <div className="as-chat-queue-header">
            <span>{conv.queueCount} queued — sends when the agent finishes</span>
            {conv.queueCount > 1 ? (
              <button type="button" onClick={conv.clearQueue} className="as-chat-queue-clear" aria-label="Clear queue">
                Clear
              </button>
            ) : null}
          </div>
          {conv.queuedTexts.map((text, i) => (
            <div key={`${i}-${text.slice(0, 16)}`} className="as-chat-queue-row" title={text}>
              <span className="as-chat-queue-text">{text}</span>
              <button
                type="button"
                onClick={() => conv.removeQueuedAt(i)}
                className="as-chat-upload-x"
                aria-label={`Remove queued message: ${text.slice(0, 60)}`}>
                <X />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {/* Status row — only while a turn is in flight. [PROD-1205] Label
          only: Stop moved into the send button, where Agent desktop keeps
          it, so the palette has one in-progress control instead of two. */}
      {isStreaming ? (
        <div className="as-chat-generating" role="status" aria-live="polite">
          <span className="as-chat-generating-spin" aria-hidden>
            <Loader2 />
          </span>
          <span className="as-chat-generating-label">{statusLabel}</span>
        </div>
      ) : null}
      <ComposerShell className="as-chat-composer-shell">
        <div className="as-chat-textarea-row">
          <ComposerTextarea
            ref={inputRef}
            value={composerText}
            onChange={(e) => setComposerText(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onFocus={() => {
              // Safety scroll for the iOS keyboard pop window — the
              // visualViewport.resize event may fire after focus, so
              // scroll the textarea into view to make sure it isn't
              // hidden behind the form-helper-bar in the meantime.
              setTimeout(() => {
                inputRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
              }, 200)
            }}
            placeholder="Ask a follow-up…"
            rows={1}
            className="!min-h-0 !max-h-32 !px-2.5 !py-2"
          />
        </div>
        {/* Action row uses the same `ComposerActionRow` primitive that
            apps/agent's chat.tsx renders, so the two surfaces share
            one canonical layout. Right = paperclip / mic / send. */}
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFilesPicked} />
        <ComposerActionRow
          density="compact"
          right={
            <>
              <ComposerIconButton density="compact" onClick={onPaperclipClick} aria-label="Attach files">
                <Paperclip />
              </ComposerIconButton>
              <VoiceButton
                onAppend={(delta) => {
                  const cur = composerText
                  const sep = cur && !/\s$/.test(cur) && !/^\s/.test(delta) ? ' ' : ''
                  setComposerText(cur + sep + delta)
                }}
              />
              {/* [PROD-1205] One button, two jobs — same as Agent desktop's
                  composer. A follow-up typed mid-turn still queues: Enter
                  sends it (see `onKeyDown`), the click target is Stop. */}
              {isStreaming ? (
                <ComposerSendButton
                  density="compact"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation()
                    void conv.cancel()
                  }}
                  aria-label="Stop">
                  <Square />
                </ComposerSendButton>
              ) : (
                <ComposerSendButton
                  density="compact"
                  disabled={sendDisabled || hasUploadingFile}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!sendDisabled && !hasUploadingFile) {
                      void conv.submit(composerText)
                    }
                  }}
                  aria-label="Send message">
                  <ArrowUp />
                </ComposerSendButton>
              )}
            </>
          }
        />
      </ComposerShell>
    </div>
  )
}
